import { useEffect, useState } from 'react'
import { Autocomplete, Box, TextField, Typography } from '@mui/material'
import Bugsnag from '@bugsnag/js'
import { AxiosError } from 'axios'
import { client } from '../../utils/client'
import { ProfessionType } from '../../utils/sharedTypes'

type ProfessionPickerPropsType = {
  value: ProfessionType | null
  onChange: (profession: ProfessionType | null) => void
  onDark: boolean
}

/** Sélecteur de profession du parcours à froid, affiché quand le lien n'en portait pas (ou en portait
 * une inconnue). Sans profession, le catalogue reste complet : le sélecteur sert à le resserrer sur ce
 * qui est réellement finançable pour le visiteur.
 *
 * La liste vient de l'endpoint public déjà utilisé par le tunnel ; on n'y filtre pas par domaine,
 * `Profession` n'en portant pas. */
const ProfessionPicker = ({ value, onChange, onDark }: ProfessionPickerPropsType) => {
  const [professions, setProfessions] = useState<ProfessionType[]>([])

  useEffect(() => {
    client
      .get('/registration/content/professions/')
      .then((resp) => {
        const data = resp.data.results as ProfessionType[]
        setProfessions(data.filter((profession) => profession.code !== 'unknown'))
      })
      .catch((err: AxiosError) => {
        // Sans liste, la page reste utilisable : elle sert le catalogue non filtré.
        Bugsnag.notify(err)
        console.error(err)
      })
  }, [])

  if (professions.length === 0) return null

  return (
    <Box sx={{ mt: 4, maxWidth: 420 }}>
      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
        Votre profession
      </Typography>
      <Autocomplete
        id="cold-start-profession"
        value={value}
        options={professions}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, selected) => option.code === selected.code}
        onChange={(_, selected) => onChange(selected)}
        renderInput={(params) => (
          <TextField {...params} placeholder="Sélectionnez votre profession" size="small" />
        )}
        sx={{
          backgroundColor: 'background.paper',
          borderRadius: 1,
          // Le hero Learning est un aplat noir : sans cette bascule, le champ y perdrait son contour.
          '& .MuiOutlinedInput-notchedOutline': onDark ? { borderColor: 'transparent' } : undefined,
        }}
      />
    </Box>
  )
}

export default ProfessionPicker
